/** Browser client for the pipeline admin API (/api/pipeline/*). Same request conventions as researchAgentApi. */

export interface PipelineControlState {
  desiredState: 'running' | 'paused' | 'maintenance';
  importMode: 'manual' | 'automatic';
  restoreGeneration: number;
  configRevision: number;
  changedByUserId: number | null;
  changedAt: number;
}

export type ObservedPipelineState = 'running' | 'idle' | 'paused' | 'draining' | 'maintenance' | 'degraded' | 'waiting_for_lease';

export interface PipelineWorkerHealth {
  tunnel?: 'up' | 'down' | 'unknown';
  publicIp?: string | null;
  publicIpLookup?: 'ok' | 'failed';
  providers?: { providers?: Record<string, { requests?: number; successes?: number }> } | Record<string, { requests?: number; successes?: number }>;
}

export interface PipelineWorkerRow {
  id: string;
  slots: number;
  capabilities: string[];
  lastHeartbeatAt: number | null;
  heartbeatAgeSeconds: number | null;
  health: PipelineWorkerHealth;
  revokedAt: number | null;
}

/** What the discovery job is doing, from the job's own state (the run row's `running` string says nothing about that). */
export type DiscoveryRunState = 'leased' | 'waiting' | 'retrying' | 'completed' | 'failed' | 'cancelled';

export interface DiscoveryRunSummary {
  jobId: number;
  runId: number | null;
  state: DiscoveryRunState;
  stateCode: string | null;
  countyFips: string | null;
  source: string | null;
  attempts: number;
  maxAttempts: number;
  workerId: string | null;
  lastError: string | null;
  partitionsAcknowledged: number;
  rowsReceived: number;
  candidatesAdded: number;
  startedAt: number | null;
  finishedAt: number | null;
  nextAttemptAt: number | null;
  leaseExpiresAt: number | null;
}

export interface PipelineStatus {
  engineV2?: import('@shared/engineV2.js').EngineV2Metrics;
  control: PipelineControlState;
  observed: { running: boolean; lastHeartbeatAt: number | null; restarts: number; observedState: ObservedPipelineState };
  counts: {
    candidatesByStatus: Record<string, number>;
    jobsByStatusAndKind: Record<string, Record<string, number>>;
    awaitingApproval: number;
    admittedToday: number;
    admittedLastHour: number;
    expired: number;
  };
  workers: PipelineWorkerRow[];
  discovery?: { byState: Record<DiscoveryRunState, number>; runs: DiscoveryRunSummary[] };
  schedules: { id: number; kind: string; enabled: boolean; nextDueAt: number; lastSuccessAt: number | null; lastFailureAt: number | null; lastError: string | null }[];
  lastBackup: { setName: string; completedAt: number | null; remoteDir: string | null } | null;
  release: { gitSha: string; verifierVersion: readonly string[]; policyVersion: string; schemaVersion: string | null };
  researchLeadFreshness: { count: number; oldestVerifiedAgeSeconds: number | null; oldest: { leadId: number; verifiedAgeSeconds: number }[] };
}

export interface PipelineCandidateRow {
  id: number;
  businessName: string;
  phone: string | null;
  city: string | null;
  state: string | null;
  category: string | null;
  status: string;
  statusReason: string | null;
  reasonCode: string | null;
  verifiedAgeSeconds: number | null;
  approval: { id: number; status: string; detail: string | null } | null;
}

export type PipelineApprovalStatus = 'awaiting_approval' | 'refreshing' | 'delayed' | 'ready' | 'imported' | 'needs_review' | 'failed' | 'cancelled';

/** One approval request, tracked on its own (the candidate may be `verifying` while its approval refreshes). */
export interface PipelineApprovalRow {
  id: number;
  candidateId: number;
  businessName: string;
  phone: string | null;
  city: string | null;
  state: string | null;
  candidateStatus: string;
  resultId: string | null;
  classification: string | null;
  reasonCode: string | null;
  status: PipelineApprovalStatus;
  detail: string | null;
  jobId: number | null;
  jobStatus: string | null;
  createdAt: number;
  updatedAt: number;
  cancellable: boolean;
  actionable: boolean;
  updatedAgeSeconds: number;
}

async function pipelineRequest<T>(path: string, options: { method?: 'GET' | 'POST'; body?: unknown } = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const response = await fetch(`/api${path}`, {
    method,
    headers: {
      Accept: 'application/json',
      ...(options.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(method !== 'GET' ? { 'X-LeadForge-Request': '1' } : {}),
    },
    body: options.body === undefined ? undefined : JSON.stringify(options.body),
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({})) as { message?: string; error?: string };
    throw new Error(payload.message ?? payload.error ?? `Request failed (${response.status})`);
  }
  return response.json() as Promise<T>;
}

export const pipelineApi = {
  status: () => pipelineRequest<PipelineStatus>('/pipeline/status'),
  pause: () => pipelineRequest<PipelineControlState>('/pipeline/pause', { method: 'POST', body: {} }),
  resume: () => pipelineRequest<PipelineControlState & { warnings?: string[] }>('/pipeline/resume', { method: 'POST', body: {} }),
  setImportMode: (mode: 'manual' | 'automatic') => pipelineRequest<PipelineControlState>('/pipeline/import-mode', { method: 'POST', body: { mode } }),
  awaitingApproval: () => pipelineRequest<{ total: number; rows: PipelineCandidateRow[] }>('/pipeline/candidates?status=pass,expired&limit=100&offset=0'),
  approve: (candidateId: number) => pipelineRequest<{ id: number; status: string; detail: string | null }>('/pipeline/approvals', { method: 'POST', body: { candidateId } }),
  approvals: (scope: 'open' | 'recent') => pipelineRequest<PipelineApprovalRow[]>(`/pipeline/approvals?status=${scope}&limit=100`),
  cancelApproval: (approvalId: number) => pipelineRequest<{ id: number; status: string; detail: string | null }>(`/pipeline/approvals/${approvalId}/cancel`, { method: 'POST', body: {} }),
};
